import { BigNumber, ethers } from "ethers";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAccount } from "../hooks/useAccount";
import { useBalance } from "../hooks/useBalance";
import { BuyMethod, useBuy } from "../hooks/useBuy";
import { useConnect } from "../hooks/useConnect";
import { useRefAddress } from "../hooks/useRefAddress";
import { NumberFormater } from "../utils/formater/NumberFormater";
import { useWeb3Ts } from "./Web3TsContext";

interface IContext {
 fromValue: number;
 toValue: number;
 setFromValue: (val: number) => void;
 balance: number;
 balanceToken: number;
 buy: () => Promise<boolean>;
 refAddr?: string;
 createInvite(): Promise<void>;
 method: BuyMethod;
 setMethod(val: BuyMethod): void;
 bnbUsdtPrice: number;
 isAllowBuy: boolean;
}
const Context = createContext<IContext>({} as IContext);
export function BuyPresaleProvider(props: React.PropsWithChildren) {
 const web3TsConfig = useWeb3Ts();
 const [method, _setMethod] = useState<BuyMethod>("bnb");
 const [fromValue, _setFromValue] = useState(0);
 const [toValue, _setToValue] = useState(0);
 const { isConnected } = useAccount();
 const { connect } = useConnect();
 const { refAddr, referral } = useRefAddress();
 const { buy: _buy } = useBuy();
 const { balance: bnbBalance, balanceToken } = useBalance();
 const [balance, setBalance] = useState<number>(0);
 const { account } = useAccount();
 const [bnbUsdtPrice, setBnbUsdtPrice] = useState<number>(0);
 const { contractUsdt, contractBuy } = useWeb3Ts();
 useEffect(() => {
  if (contractBuy) {
   contractBuy
    .getBNBUSDTPrice()
    ?.then((resp: BigNumber) => {
     const value = ethers.utils.formatUnits(resp);
     setBnbUsdtPrice(+value);
    })
    .catch(console.error);
  }
 }, [contractBuy]);
 function loadBalance(
  fromValue: number,
  method: string,
  bnbBalance: number,
  contractUsdt: ethers.Contract | undefined,
  account: string | undefined
 ) {
  setFromValue(fromValue);
  if (method === "bnb") {
   let value = bnbBalance - (method === "bnb" ? web3TsConfig.gasFee : 0);
   if (value < 0) value = 0;
   setBalance(NumberFormater.roundUp(value, 4));
  } else {
   setBalance(0);
   contractUsdt
    ?.balanceOf(account)
    .then((resp: BigNumber) => {
     const value = ethers.utils.formatUnits(resp);
     setBalance(+value);
    })
    .catch(() => setBalance(0));
  }
 }

 useEffect(() => {
  loadBalance(fromValue, method, bnbBalance, contractUsdt, account);
 }, [method, isConnected, bnbBalance, contractUsdt]);
 const setFromValue = (val: number) => {
  _setFromValue(val);
  const x =
   method === "bnb"
    ? val * (web3TsConfig.buyMetadata.salePrice ?? 0)
    : val * (web3TsConfig.buyMetadata.salePriceUSDT ?? 0);
  const toVal = NumberFormater.roundUp(x, 3);
  _setToValue(toVal);
 };
 const buy = async () => {
  return _buy({
   value: fromValue,
   method,
  }).finally(() => {
   loadBalance(fromValue, method, bnbBalance, contractUsdt, account);
  });
 };
 const setMethod = (val: BuyMethod) => {
  if (val === "usdt") {
   if (!isConnected) {
    return connect();
   }
  }
  _setMethod(val);
 };

 const createInvite = async () => {
  if (!isConnected) {
   return connect();
  }
  navigator.clipboard.writeText(referral || "");
  toast.success("Copied to clipboard", {});
 };

 const isAllowBuy = useMemo(() => {
  if (fromValue <= 0) return false;
  if (!isConnected) return false;
  if (balance < fromValue) return false;
  return true;
 }, [balance, isConnected, fromValue]);
 return (
  <Context.Provider
   value={{
    fromValue,
    refAddr,
    setFromValue,
    toValue,
    balance,
    balanceToken,
    buy,
    createInvite,
    method,
    setMethod,
    bnbUsdtPrice,
    isAllowBuy,
   }}
  >
   {props.children}
  </Context.Provider>
 );
}

export function useBuyPresale() {
 return useContext(Context);
}
